import React, { useState } from "react";

import classnames from "classnames";
import camelCase from "lodash/camelCase";
import isEmpty from "lodash/isEmpty";
import mapKeys from "lodash/mapKeys";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { useEnableRestrictSonderSignup } from "components/hooks/feature-flags";
import { useTranslation } from "components/i18n/source";
import { useAppContext } from "components/store/source";
import { Button, Form, Input, Section, Text } from "components/ui/source";

import { IconEye, IconEyeStrike } from "icons";

import AccountsApi from "../../shared/api/AccountsApi";
import { messageMahalo, messageType } from "../../util/messageMahalo";

import OAuthContainer from "./OAuthContainer";
import ResetPasswordForm from "./ResetPasswordForm";
import { useFlow } from "./utils";
import { validateLoginForm } from "./validator";

import styles from "./Accounts.module.scss";

function LoginForm({
  openSignUpPopup,
  closePopup,
  setLoading,
  logSubmit,
  initialEmail,
  fromSignUp,
  redirectPath,
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const {
    signUpPopupProps,
    loginPopupProps,
    setShowResetPassword,
    allowSignUp,
  } = useAppContext();
  const flow = useFlow();
  const [error, setError] = useState(
    fromSignUp ? flow.loginDefaultErrorMessage : null,
  );
  const [showPasswordAsText, setShowPasswordAsText] = useState(false);

  const prefix = "modals.accounts.logIn";

  // Determine if there is a titleKey prop prior to default
  const titleKey =
    loginPopupProps.titleI18nKey ??
    (fromSignUp ? `${prefix}.titleFromSignUp` : `${prefix}.title`);

  const enableRestrictSonderSignup = useEnableRestrictSonderSignup();

  const showSignUpLink = enableRestrictSonderSignup
    ? allowSignUp(loginPopupProps?.source)
    : loginPopupProps.showSignUpLink ?? true;

  async function onSubmitEmail(account, { setErrors }) {
    setLoading(true);

    // Send login request
    const response = await AccountsApi.login(account);
    let body;
    try {
      // The response body might be empty for unknown errors
      body = await response.json();
    } catch (e) {
      body = "";
    }

    if (body.error) {
      if (body.message) {
        setError(body.message);
      } else {
        setError(body.error);
      }
    } else if (body.errors) {
      const errors = body.errors;
      if (Object.keys(errors).length) {
        setErrors(mapKeys(errors, (_, key) => camelCase(key)));
      } else {
        setError(t("modals.accounts.errors.etc"));
      }
    } else if (!response.ok) {
      setError(t("modals.accounts.errors.etc"));
    } else {
      await logSubmit("email_login", account.email);

      const jwt = response.headers.get("Authorization").split("Bearer ")[1];
      messageMahalo({
        type: messageType.authToken,
        body: jwt,
        isNewAccount: false,
      });

      if (redirectPath) {
        router.push(redirectPath);
      } else {
        router.reload();
      }
    }

    setLoading(false);
  }

  if (loginPopupProps.showResetPassword) {
    return (
      <ResetPasswordForm
        onRequestBack={() => setShowResetPassword(false)}
        onRequestClose={closePopup}
        setLoading={setLoading}
      />
    );
  }

  const passwordHide = (
    <div
      className={styles.eyeStrikeContainer}
      onClick={() => setShowPasswordAsText(false)}
    >
      <IconEyeStrike size="lg" />
    </div>
  );
  const passwordShow = (
    <div
      className={styles.eyeIconContainer}
      onClick={() => setShowPasswordAsText(true)}
    >
      <IconEye size="lg" />
    </div>
  );
  return (
    <>
      <Text
        id="loginFormHeading"
        element="h4"
        className={classnames(styles.header, styles.centered_text)}
        types={["h4", "medium"]}
        translateKey={titleKey}
      />

      <Text
        className={classnames(
          styles.error_message,
          styles.centered_text,
          error && styles.visible,
        )}
        types={["subtitles", "accent-red"]}
      >
        {error}
      </Text>

      <Form
        className={styles.form_container}
        validate={validateLoginForm}
        initialValues={{ email: initialEmail }}
        initialErrors={{ email: "" }}
        onSubmit={onSubmitEmail}
        validateOnChange={true}
      >
        {({ handleSubmit, errors }) => (
          <>
            <Input
              placeholder={t("modals.accounts.email.email")}
              type="email"
              name="email"
              theme="popup"
              required
            />
            <Input
              placeholder={t("modals.accounts.email.password")}
              type={showPasswordAsText ? "text" : "password"}
              name="password"
              theme="popup"
              inputIcon={showPasswordAsText ? passwordHide : passwordShow}
              required
            />

            <Section
              className={classnames(
                styles.centered_text,
                styles.forgot_password,
              )}
            >
              <Text
                translateKey={`${prefix}.action.resetPasswordMessage`}
                types={["subtitles", "cancel"]}
              />
              &nbsp;
              <Button
                type="no_background"
                textProps={{
                  translateKey: `${prefix}.action.resetPassword`,
                  types: ["subtitles", "underline", "cancel"],
                }}
                upperCase={false}
                onClick={() => setShowResetPassword(true)}
              />
            </Section>

            <Button
              htmlType="submit"
              textProps={{
                translateKey: `${prefix}.email.cta`,
                types: ["subtitle"],
              }}
              upperCase={false}
              onClick={handleSubmit}
              disabled={!isEmpty(errors)}
            />
          </>
        )}
      </Form>

      <Text
        className={classnames(styles.centered_text, styles.login_separator)}
        types={["subtitles"]}
        translateKey={`${prefix}.oAuth.text`}
      />

      <OAuthContainer
        setLoading={setLoading}
        theme="login"
        logSubmit={logSubmit}
        exitIntentProps={fromSignUp ? signUpPopupProps : {}}
        redirectPath={redirectPath}
      />

      {showSignUpLink ? (
        <Section className={styles.centered_text}>
          <Text
            translateKey={`${prefix}.action.signUpMessage`}
            types={["subtitles"]}
          />
          &nbsp;
          <Button
            type="no_background"
            textProps={{
              translateKey: `${prefix}.action.signUp`,
              types: ["subtitles", "underline"],
            }}
            upperCase={false}
            onClick={() => openSignUpPopup()}
          />
        </Section>
      ) : null}
    </>
  );
}

LoginForm.propTypes = {
  openSignUpPopup: PropTypes.func.isRequired,
  closePopup: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  logSubmit: PropTypes.func.isRequired,
  initialEmail: PropTypes.string,
  exitIntentProps: PropTypes.object,
  flow: PropTypes.number,
  fromSignUp: PropTypes.bool,
  redirectPath: PropTypes.string,
};

LoginForm.defaultProps = {
  openSignUpPopup: () => {},
  closePopup: () => {},
  setLoading: () => {},
  logSubmit: async () => {},
  initialEmail: "",
  exitIntentProps: {},
  fromSignUp: false,
  redirectPath: null,
};

export default LoginForm;
